import { existsSync } from "fs";
import { resolve } from "path";

import type { GencowCtx } from "@gencow/core";
import { isAdminIdentifier, quoteAdminIdentifier } from "./admin-sql-guard.js";
import { applyAdminAccessMiddleware, type AdminAccessPolicy } from "./server-admin-access.js";
import { redactAdminLogValue } from "./server-admin-redaction.js";

type RawSqlFn = (sql: string, params?: any[]) => Promise<any[]>;

type RouteRegistrar = {
  use?(path: string, handler: any): void;
  post(path: string, handler: (context: any) => Promise<unknown> | unknown): void;
};

const SYSTEM_TABLES = new Set([
  "user",
  "session",
  "account",
  "verification",
  "_system_files",
  "_system_settings",
  "__drizzle_migrations",
  "drizzle_migrations",
]);

export function validateResetConfirmation(confirm: unknown): boolean {
  return confirm === "delete all data";
}

export function filterResettableTables(tableRows: Array<Record<string, string>>): string[] {
  return tableRows
    .map((row) => row.table_name)
    .filter((tableName) => !SYSTEM_TABLES.has(tableName))
    .filter(isAdminIdentifier);
}

export function resolveSeedFile(functionsPath: string): string | null {
  const seedTs = resolve(functionsPath, "seed.ts");
  const seedJs = resolve(functionsPath, "seed.js");
  let seedFile = existsSync(seedTs) ? seedTs : existsSync(seedJs) ? seedJs : null;

  if (!seedFile && functionsPath.endsWith("gencow-bc")) {
    const originalDir = resolve(functionsPath, "..", "gencow");
    const origTs = resolve(originalDir, "seed.ts");
    const origJs = resolve(originalDir, "seed.js");
    seedFile = existsSync(origTs) ? origTs : existsSync(origJs) ? origJs : null;
  }

  return seedFile;
}

export async function runSeedFile(
  functionsPath: string,
  getCtx: () => GencowCtx,
  importModule: (path: string) => Promise<any> = (path) => import(path),
): Promise<boolean> {
  const seedFile = resolveSeedFile(functionsPath);
  if (!seedFile) {
    console.log("[seed] gencow/seed.ts not found — skipping");
    return false;
  }

  console.log(`[seed] Running ${seedFile}...`);
  const seedModule = await importModule(seedFile);
  const seedFn = seedModule.default;
  if (typeof seedFn !== "function") {
    console.warn("[seed] seed.ts has no default export function — skipping");
    return false;
  }

  await seedFn(getCtx());
  console.log("[seed] Seed completed ✓");
  return true;
}

export function registerAdminMaintenanceRoutes(params: {
  admin: RouteRegistrar;
  rawSql: RawSqlFn;
  getCtx: () => GencowCtx;
  functionsPath?: string;
  accessPolicy?: AdminAccessPolicy;
}): void {
  applyAdminAccessMiddleware(params.admin, params.accessPolicy);

  params.admin.post("/reset", async (c) => {
    const body = (await c.req.json().catch(() => ({}))) as Record<string, unknown>;
    if (!validateResetConfirmation(body.confirm)) {
      return c.json(
        {
          error: 'Safety check: body must include { "confirm": "delete all data" }',
        },
        400,
      );
    }

    try {
      const tableRows = await params.rawSql(`
                SELECT table_name FROM information_schema.tables
                WHERE table_schema = 'public'
                AND table_type = 'BASE TABLE'
            `);
      const userTables = filterResettableTables(tableRows as Array<Record<string, string>>);

      const truncated: string[] = [];
      for (const table of userTables) {
        await params.rawSql(`TRUNCATE TABLE ${quoteAdminIdentifier(table)} CASCADE`);
        truncated.push(table);
      }
      console.log(`[admin] TRUNCATE ${truncated.length} tables: ${truncated.join(", ")}`);

      let seeded = false;
      if (params.functionsPath) {
        seeded = await runSeedFile(params.functionsPath, params.getCtx);
      }

      return c.json({ success: true, truncated, seeded });
    } catch (error: unknown) {
      const message = redactAdminLogValue(error);
      console.error("[admin] reset failed:", message);
      return c.json({ error: message }, 500);
    }
  });

  params.admin.post("/seed", async (c) => {
    if (!params.functionsPath) {
      return c.json({ error: "Functions path not configured" }, 500);
    }

    try {
      const seeded = await runSeedFile(params.functionsPath, params.getCtx);
      if (!seeded) {
        return c.json(
          {
            success: false,
            error: "gencow/seed.ts not found. Create it to define seed data.",
            hint: "export default async function seed(ctx) { await ctx.db.insert(...) }",
          },
          404,
        );
      }
      return c.json({ success: true });
    } catch (error: unknown) {
      const message = redactAdminLogValue(error);
      console.error("[admin] seed failed:", message);
      return c.json({ error: message }, 500);
    }
  });
}
